import { Dialog, DropdownButton, DropdownMenuItem, colorValue } from '@hai/ui-react';
import { isNil } from 'ramda';
import React, { createContext, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Status } from 'src/js/component/actionBar/actionBar';
import { NoContentAvailable } from 'src/js/component/no-content-available';
import { SortType } from 'src/js/component/sort-manager';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useModalControls } from 'src/js/hook/use-modal-controls';
import { useStores } from 'src/js/hook/use-stores';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { StreamingActionBar } from 'src/js/component/actionBar/streaming-action-bar';
//import { fetchVideoInputs } from 'src/js/pages/streaming/video-input/video-input-store';
import { useWatchObject } from 'src/js/store/use-watch';
import { constant } from 'src/js/constant';
//import { AddSt2110 } from './add-st2110-modal';
import { St2110List } from './st2110-list';
import { St2110ViewModel, st2110IsStarted, st2110IsStopped, st2110ModelToStatus } from './st2110-models';
import st2110Models, { deleteSt2110, fetchSt2110, resetSt2110Statistics, startStopSt2110 } from './st2110-store';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath } from 'src/js/api/route-path-index';
import { ServicesResponse } from 'src/js/model/api/response/services-response';
import { useLocalStorage } from 'usehooks-ts';
import { isNilOrEmpty } from 'src/js/util/global-util';

export const St2110sCtx = createContext(undefined);
export const NmosServiceActiveContext = React.createContext(false);

let connectionLost = false;
export const St2110Page: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const [st2110s, setSt2110s] = useState<St2110ViewModel[]>(st2110Models);
  const [initialized, setInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [nmosServiceActive, setNmosServiceActive] = useState(false);
  const fetchServices = () =>
    apiClientWithoutHandler.genericController.get<any>(APIPath.services.status).then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          const services = response as ServicesResponse;
          if (services.nmos && services.status?.nmos) {
            setNmosServiceActive(true);
          } else {
            setNmosServiceActive(false);
          }
        }
      }
      return res;
    });

  const interval = constant.defaultRefreshInterval;
  useAdaptiveFetch(fetchServices, interval * 10);

  const updateSt2110List = (updates: St2110ViewModel[]) => {
    setSt2110s((existing) => {
      return updates.map((st2110) => {
        const item = existing.find((i) => i.id == st2110.id); // TODO we need some uuid in a private field for deletable objects
        if (item) {
          st2110.selected = item.selected;
          st2110.hidden = item.hidden;
        }
        return st2110;
      });
    });
  };
  useWatchObject(st2110Models, updateSt2110List);

  useAdaptiveFetch(
    () =>
      fetchSt2110(t).then((res) => {
        if (res.ok) {
          setInitialized(true);
          setIsLoading(false);
          if (connectionLost) {
            triggerEvent('connection-restored');
            connectionLost = false;
          }
        } else if (res.problem === 'NETWORK_ERROR') {
          triggerEvent('connection-lost');
          connectionLost = true;
        }
      }),
    5000,
  );

  const fetchData = async () => {
    // decoders? await fetchVideoInputs();
    //fetchSt2110Resolutions();
  };

  useEffect(() => {
    fetchData();
  }, []);

  watchEvent('preset-load', () => {
    fetchSt2110(t);
    fetchData();
  });

  const filteredStart = () => (nmosServiceActive ? [] : st2110s.filter((e) => e.selected && st2110IsStopped(e.state)));
  const handleStartEssense = () => {
    const filtered = filteredStart();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopSt2110(e, true, true, false, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        const success = responses.filter((r) => r.ok);
        if (failed.length >= 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('st2110.notifications.startError', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
        if (success.length > 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('st2110.notifications.startSuccess', { count: success.length }),
            NotificationVariant.SUCCESS,
            t,
          );
        } else if (success.length === 1) {
          createAndDispatchNotification(
            t(
              isNilOrEmpty(responses[0].data.info.name)
                ? 'st2110.notifications.startSuccessEmptyName'
                : 'st2110.notifications.startSuccess',
              {
                name: filtered[0].name,
              },
            ),
            NotificationVariant.SUCCESS,
            t,
          );
        }
      });
    }
  };

  const filteredStop = () => (nmosServiceActive ? [] : st2110s.filter((e) => e.selected && st2110IsStarted(e.state)));
  const handleStopEssense = () => {
    const filtered = filteredStop();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => startStopSt2110(e, false, true, false, t))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        const success = responses.filter((r) => r.ok);
        if (failed.length >= 1) {
          // Message also in start/stop
          createAndDispatchNotification(
            t('st2110.notifications.stopError', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
        if (success.length > 1) {
          createAndDispatchNotification(
            t('st2110.notifications.stopSuccess', { count: responses.length }),
            NotificationVariant.SUCCESS,
            t,
          );
        } else if (success.length === 1) {
          createAndDispatchNotification(
            t(
              isNilOrEmpty(responses[0].data.info.name)
                ? 'st2110.notifications.stopSuccessEmptyName'
                : 'st2110.notifications.stopSuccess',
              {
                name: filtered[0].name,
              },
            ),
            NotificationVariant.SUCCESS,
            t,
          );
        }
      });
    }
  };

  // UNUSED
  const [deleteShown /*showDelete*/, , hideDelete] = useModalControls();
  const filteredDelete = () => st2110s.filter((e) => e.selected);
  const handleDeleteEssense = () => {
    const filtered = filteredDelete();
    if (filtered.length > 0) {
      Promise.all(filtered.map((e) => deleteSt2110(e))).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 0) {
          createAndDispatchNotification(
            t('st2110.notifications.deleteError', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        }
      });
    }
  };

  const filteredReset = () => st2110s.filter((e) => e.selected);
  const handleResetEssense = () => {
    const filtered = filteredReset();
    if (filtered.length > 0) {
      Promise.all(
        filtered.map((e) =>
          resetSt2110Statistics(e, t, () => {
            // console.log('ok')
          }),
        ),
      ).then((responses) => {
        triggerEvent('preset-update');
        const failed = responses.filter((r) => !r.ok);
        if (failed.length > 0) {
          createAndDispatchNotification(
            t('st2110.notifications.resetStatsError', { count: failed.length }),
            NotificationVariant.ERROR,
            t,
          );
        } else {
          createAndDispatchNotification(
            t('st2110.notifications.resetSelectedSuccess', { count: filtered.length }),
            NotificationVariant.SUCCESS,
            t,
          );
        }
      });
    }
  };

  const statusTypes: Status[] = [
    { status: 'ACTIVE', color: colorValue('haiui-green-01') },
    { status: 'ALERT', color: colorValue('haiui-amber-01') },
    { status: 'INACTIVE', color: colorValue('haiui-gray-07') },
  ];

  const handleSelect = (item: St2110ViewModel, selected: boolean) => {
    const Essenses = st2110s.map((i: St2110ViewModel) => (i.id === item.id ? { ...i, selected } : i));
    setSt2110s(Essenses);
  };

  const onSelectAll = (selected: boolean) => {
    const models = st2110s.map((i) => ({ ...i, selected }));
    setSt2110s(models);
  };

  const [searchFilters, setSearchFilters] = useLocalStorage(
    `${constant.lStorage.searchFilter}:${sessionStore.username}:st2110`,
    [],
  );

  const sortInfo = {
    id: { title: t('sort.sortText.id'), type: SortType.GENERIC },
    name: { title: t('sort.sortText.name'), type: SortType.NATURAL_SORT },
  };

  const dropdownList = {
    audio: { title: t('st2110.mediaTypes.audio'), type: SortType.GENERIC },
    video: { title: t('st2110.mediaTypes.video'), type: SortType.NATURAL_SORT },
    data: { title: t('st2110.mediaTypes.data'), type: SortType.NATURAL_SORT },
  };
  const allItems = ['audio', 'video', 'data'];
  const [toggleView, setToggleView] = useLocalStorage<string[]>(
    `${constant.lStorage.searchFilter}:${sessionStore.username}:st2110-filter`,
    allItems,
  );

  // const [toggleViewState, setToggleViewState] = useMutable<string[]>(allItems);
  /* UNUSED haiui is buggy
  const onToggleView = (e: any[]) => {
    //console.log('onToggleView', e,  toggleViewState());

    if (Array.isArray(e) && e.length >= 0) {
      const nowSelected = e.map((e) => e.eventKey);
      const addedItems = getAddedElement(toggleView, nowSelected);
      const removedItems = getRemovedElement(toggleView, nowSelected);

      if (addedItems.includes('all')) {
        // console.log('setToggleView', allItems);
        setToggleView(allItems);
        // setToggleViewState(allItems);
        return;
      } else if (removedItems.includes('all')) {
        setToggleView([]);
        // setToggleViewState([]);
        return;
      } else if (addedItems.length > 0 && nowSelected.includes('all')) {
        // the user clicked an invididual item
        // remove all and go with individuals
        const withAllRemoved = nowSelected.filter((n) => n !== 'all');
        // console.log('setToggleView', withAllRemoved);
        setToggleView(withAllRemoved);
        // setToggleViewState(withAllRemoved);
        return;
      } else if (removedItems.length > 0 && nowSelected.includes('all')) {
        // the user clicked an invididual item
        // remove all and go with individuals
        const withAllRemoved = nowSelected.filter((n) => n !== 'all');
        // console.log('setToggleView', withAllRemoved);
        setToggleView(withAllRemoved);
        // setToggleViewState(withAllRemoved);
        return;
      } else if (removedItems.length > 0 || addedItems.length > 0) {
        // console.log('setToggleView', nowSelected);
        setToggleView(nowSelected);
        // setToggleViewState(nowSelected);
      }
    }
  };*/

  const onToggleItem = (key: string, _event: any) => {
    if (toggleView.includes(key)) {
      // remove item
      setToggleView(toggleView.filter((e) => e !== key));
    } else {
      // add item
      setToggleView([...toggleView, key]);
    }
  };

  ///console.log('render with toggled', toggleView)
  const toggleViewDropdown = (): ReactNode => {
    const title =
      toggleView.length === 0
        ? t('st2110.dropdown.noOutputs')
        : toggleView.length === allItems.length
        ? t('st2110.dropdown.title.all')
        : toggleView.map((v) => t(`st2110.dropdown.mediaTypes.${v}`)).join(', ');
    return (
      <DropdownButton
        title={title}
        // BUGGY onChange={onToggleView}
        multiSelect={true}
        label={t('st2110.dropdown.label')}
        alignLabel="right"
        floatRight
        menuMaxHeight="none"
      >
        {/*<DropdownMenuItem
          title={t('st2110.dropdown.allOutputs')}
          eventKey="all"
          checkbox
          className="mk-bottom-line"
          checked={toggleView.includes('all') ? true : toggleView.length > 0 ? 'indeterminate' : false}
        />*/}
        {Object.keys(dropdownList).map((key) => {
          const item = dropdownList[key as 'audio'];
          return (
            <DropdownMenuItem
              key={key}
              title={item.title}
              eventKey={key}
              checked={toggleView.includes(key)}
              checkbox={true}
              section="items"
              onSelect={onToggleItem}
            />
          );
        })}
      </DropdownButton>
    );
  };

  return (
    <St2110sCtx.Provider value={{ st2110s, setSt2110s }}>
      <NmosServiceActiveContext.Provider value={nmosServiceActive}>
        {initialized && (isNil(st2110s) || st2110s.length === 0) ? (
          <NoContentAvailable
            title={t('st2110.noContent.title')}
            subText={t('st2110.noContent.subText')}
            //btnText={t('st2110.noContent.btnText')}
            iconName="Streams"
            btnDisabled={sessionStore.isUser() || nmosServiceActive}
          />
        ) : (
          <StreamingActionBar
            items={st2110s}
            isLoading={isLoading}
            //showPlaceholder={false}
            searchKeys={['name', 'addressTxt', 'typeTxt', 'rtpPayload']}
            initialSearchFilters={searchFilters}
            onChangeSearchFilters={setSearchFilters}
            customDropdown={toggleViewDropdown}
            sortInfo={sortInfo}
            defaultSortKey="id"
            viewInfo={toggleView}
            statusTypes={statusTypes}
            getStateValue={st2110ModelToStatus}
            actionButtons={
              sessionStore.isUser()
                ? []
                : [
                    {
                      onClick: handleStartEssense,
                      counter: filteredStart,
                      label: t('general.start'),
                    },
                    { onClick: handleStopEssense, counter: filteredStop, label: t('general.stop') },
                    { onClick: handleResetEssense, counter: filteredReset, label: t('general.reset') },
                    // no delete on decoder  { onClick: showDelete, counter: filteredDelete, label: t('general.deleteBtn') },
                  ]
            }
            List={St2110List}
            onSelectAll={onSelectAll}
            updateSelection={handleSelect}
            storageKey="st2110"
            nmosServiceActive={nmosServiceActive}
            placeholderRightComponents={1}
            placeholderNumPanels={4}
          />
        )}
        {deleteShown && (
          <Dialog
            headline={t('st2110.fields.modal.delete.title', { count: filteredDelete().length })}
            bodyText={t('general.cannotUndo')}
            onClose={hideDelete}
            show={true}
            buttons={[
              { label: t('general.cancel') },
              { label: t('general.deleteBtn'), variant: 'danger', onClick: handleDeleteEssense },
            ]}
          />
        )}
        {/*addModalShown && (
          <Dialog
            title={t('st2110.fields.addSt2110')}
            dialogType="activity"
            headerIcon="Input"
            accentColor={constant.productColor}
            size="lg"
            className="mk-three-columns"
            content={<AddSt2110 model={newModel} ref={addSt2110Ref} nmosServiceActive={nmosServiceActive} />}
            onClose={hideAddModal}
            {...({onKeyUp:handleOnKeyUp})}
            buttons={[
              { variant: 'secondary', onClick: hideAddModal, label: t('general.cancel'), disabled: nmosServiceActive },
              {
                id: 'st2110-add-form-submit',
                variant: 'primary',
                state: addButtonState,
                close: false,
                onClick: handleAddSt2110,
                label: t('st2110.fields.addSt2110'),
                disabled: nmosServiceActive,
              },
            ]}
          />
        )*/}
      </NmosServiceActiveContext.Provider>
    </St2110sCtx.Provider>
  );
};
